/**
 * Full home pages for each chapter: TTC, Pregnancy, Motherhood, Menopause, First Chapter.
 */
import React from 'react';
import { CHAPTER_THEMES } from '../../data/chapterThemes';
import HomePageHeader from '../../components/home/HomePageHeader';
import HeroStatusCard from '../../components/home/HeroStatusCard';
import NovaCheckIn from '../../components/home/NovaCheckIn';
import { PrimaryWidget, ProgressWidget, WomenLikeYou, LiveRooms, CommunityPreview } from '../../components/home/DashboardWidgets';
import { demoChapterStackColumnStyle, DEMO_SECTION_CARD_STACK_GAP_PX } from '../../designSystem';

function gridStyle(m) {
  return { display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(2, 1fr)', gap: DEMO_SECTION_CARD_STACK_GAP_PX };
}

export function TTCHomePage({ navigate, m, hideTopHeader }) {
  const t = CHAPTER_THEMES.ttc;
  const nav = navigate || (() => {});
  return (
    <div style={demoChapterStackColumnStyle}>
      {!hideTopHeader && (
        <>
          <HomePageHeader pageTitle="Your TTC Hub" theme={t} statusPillText="Cycle day 14 · Fertile window" onSignUp={nav} />
          <HeroStatusCard
            theme={t}
            eyebrow="CYCLE DAY 14 · TTC MONTH 4 · FERTILE WINDOW"
            headline="Your best window is open, Maya."
            subline="Month 4 — you've tracked every cycle, shown up every day. Today and tomorrow are peak."
            pills={[
              { label: 'Peak fertility', dot: true, dotColor: 'bg-orange-400' },
              { label: 'OPK: check today' },
              { label: '4 months trying' },
            ]}
            linkLabel="My cycle →"
            onLink={() => nav('/demo/trying-to-conceive/cycle')}
          />
          <NovaCheckIn
            theme={t}
            chapter="ttc"
            context="fertile window check-in"
            message="Today is peak fertile window — cycle day 14. Did you test with an OPK this morning? I can help you read the result."
            onOpenNova={() => nav('/nova')}
          />
        </>
      )}
      <div style={gridStyle(m)}>
        <PrimaryWidget
          theme={t}
          title="Cycle status"
          descriptor="Where you are in your cycle — timing, window, and how long you’ve been trying."
          linkLabel="See tracker →"
          onLink={() => nav('/demo/trying-to-conceive/cycle')}
          rows={[
            { label: 'Cycle day', value: '14' },
            { label: 'Fertile window', value: 'Days 13–15' },
            { label: 'Next period est.', value: 'Apr 6' },
            { label: 'Months trying', value: '4 mo' },
          ]}
        />
        <ProgressWidget
          theme={t}
          title="Fertile window · day 2 of 3"
          descriptor="Peak day today · TWW starts Apr 16"
          steps={[
            { number: 1, label: 'Cycle start', done: true },
            { number: 2, label: 'OPK track', done: true },
            { number: 3, label: 'Peak day', active: true },
            { number: 4, label: 'TWW' },
            { number: 5, label: 'Test day' },
          ]}
          linkLabel="See journey →"
          onLink={() => nav('/demo/ttc/myjourney')}
        />
      </div>
      <div style={gridStyle(m)}>
        <WomenLikeYou
          theme={t}
          descriptor="People Nova matched to your timeline and situation — not a generic list."
          users={[
            { name: 'Sarah K.', description: 'Cycle 14 · peak OPK today · Austin TX', initial: 'S' },
            { name: 'Priya M.', description: 'Month 4 · natural cycles · Seattle', initial: 'P' },
          ]}
          onSeeAll={() => nav('/demo/ttc/connect')}
        />
        <LiveRooms
          theme={t}
          descriptor="Real-time rooms in your chapter — jump in when you need company."
          rooms={[
            { name: 'Fertile window timing', count: 12 },
            { name: 'OPK reading help', count: 8 },
          ]}
          onSeeAll={() => nav('/demo/ttc/community')}
        />
      </div>
      <CommunityPreview
        theme={t}
        descriptor="Posts from people on a similar timeline — same chapter, same chapter energy."
        posts={[
          { authorName: 'Sarah K.', authorInitial: 'S', timeAgo: '2h ago', text: "PEAK OPK today!! Anyone else testing positive on cycle day 14? This is my 4th month and first time getting such a strong positive 🙏", likes: 34, sameBoat: 21, replies: 8 },
          { authorName: 'Jamie R.', authorInitial: 'J', timeAgo: '4h ago', text: "Waiting for the TWW to end feels like forever. Anyone have good distractions? Trying not to symptom-spot 😅", likes: 18, sameBoat: 41, replies: 5 },
        ]}
        onSeeAll={() => nav('/demo/ttc/community')}
      />
    </div>
  );
}

export function PregnancyHomePage({ navigate, m, hideTopHeader }) {
  const t = CHAPTER_THEMES.pregnancy;
  const nav = navigate || (() => {});
  return (
    <>
      {!hideTopHeader && (
        <>
          <HomePageHeader pageTitle="Your Pregnancy Hub" theme={t} statusPillText="Week 20 · Second trimester" onSignUp={nav} />
          <HeroStatusCard
            theme={t}
            eyebrow="WEEK 20 · SECOND TRIMESTER · DAY 3"
            headline="Halfway there, Maya."
            subline="Anatomy scan this week · Baby moving more · Glucose test in 4 weeks"
            pills={[
              { label: '20 weeks', dot: true, dotColor: 'bg-mint-400' },
              { label: 'Anatomy scan: Apr 6' },
              { label: '140 days to go' },
            ]}
            linkLabel="Week by week →"
            onLink={() => nav('/demo/pregnancy/myjourney')}
          />
          <NovaCheckIn
            theme={t}
            context="morning check-in"
            message="Week 20 is a milestone — you're halfway! Your anatomy scan is Apr 6. Any questions about what they'll check?"
            onOpenNova={() => nav('/nova')}
          />
        </>
      )}
      <div style={{ ...gridStyle(m), marginTop: 16 }}>
        <PrimaryWidget
          theme={t}
          title="This week"
          descriptor="Baby size, gestational age, and what’s next on your calendar."
          linkLabel="See full week →"
          onLink={() => nav('/demo/pregnancy/myjourney')}
          rows={[
            { label: 'Baby size', value: '🍌 Banana · 10oz' },
            { label: 'Weeks pregnant', value: '20 wks, day 3' },
            { label: 'Days to due date', value: '140 days' },
            { label: 'Next appt', value: 'Apr 6 · Anatomy scan' },
          ]}
        />
        <ProgressWidget
          theme={t}
          title="2nd trimester · week 7 of 14"
          descriptor="Weeks 14–27 · Anatomy scan this week"
          steps={[
            { number: 1, label: '1st tri', done: true },
            { number: 2, label: 'Wks 14–16', done: true },
            { number: 3, label: 'Wks 17–20', active: true },
            { number: 4, label: 'Anatomy' },
            { number: 5, label: 'Glucose' },
            { number: 6, label: '3rd tri' },
          ]}
          linkLabel="See milestones →"
          onLink={() => nav('/demo/pregnancy/myjourney')}
        />
      </div>
      <div style={{ ...gridStyle(m), marginTop: 16 }}>
        <WomenLikeYou
          theme={t}
          descriptor="People Nova matched to your week and stage — swap stories without explaining your whole chart."
          users={[
            { name: 'Ava K.', description: 'Week 20 · anatomy scan week · Cincinnati', initial: 'A' },
            { name: 'Sofia M.', description: 'Week 28 · 3rd trimester · Chicago', initial: 'S' },
          ]}
          onSeeAll={() => nav('/demo/pregnancy/connect')}
        />
        <LiveRooms
          theme={t}
          descriptor="Live conversations in your chapter — scan prep, symptoms, and real-time company."
          rooms={[
            { name: 'Week 20 anatomy scan prep', count: 14 },
            { name: '2nd trimester symptoms', count: 9 },
          ]}
          onSeeAll={() => nav('/demo/pregnancy/community')}
        />
      </div>
      <CommunityPreview
        theme={t}
        descriptor="What your pregnancy chapter is talking about right now."
        posts={[
          { authorName: 'Ava K.', authorInitial: 'A', timeAgo: '2h ago', text: "Anatomy scan in 2 hours and I'm a nervous wreck. Anyone else feel more anxious for this one than the 12-week? 🩶", likes: 78, sameBoat: 52, replies: 14 },
          { authorName: 'Sofia M.', authorInitial: 'S', timeAgo: '5h ago', text: 'Week 28 and officially in the third trimester! Baby kicks so much now. 🤰', likes: 134, sameBoat: 31, replies: 9 },
        ]}
        onSeeAll={() => nav('/demo/pregnancy/community')}
      />
    </>
  );
}

export function MotherhoodHomePage({ navigate, m, hideTopHeader }) {
  const t = CHAPTER_THEMES.motherhood;
  const nav = navigate || (() => {});
  return (
    <>
      {!hideTopHeader && (
        <>
          <HomePageHeader pageTitle="Your Motherhood Hub" theme={t} statusPillText="Sofia · 6 weeks · Postpartum" onSignUp={nav} />
          <HeroStatusCard
            theme={t}
            eyebrow="POSTPARTUM · WEEK 6 · SOFIA"
            headline="Week 6 with Sofia, Maya."
            subline="6-week check-up this week · PPD screening · You're doing amazing"
            pills={[
              { label: '6 weeks old', dot: true, dotColor: 'bg-violet-400' },
              { label: '6-week check-up due' },
              { label: 'PPD screening today' },
            ]}
            linkLabel="My baby →"
            onLink={() => nav('/demo/motherhood/baby')}
          />
          <NovaCheckIn
            theme={t}
            context="week 6 check-in"
            message="Sofia is 6 weeks old — huge milestone. Your check-up this week covers physical healing, contraception, and mood. How are you really doing — not just the baby?"
            onOpenNova={() => nav('/nova')}
          />
        </>
      )}
      <div style={{ ...gridStyle(m), marginTop: 16 }}>
        <PrimaryWidget
          theme={t}
          title="Today with Sofia"
          descriptor="Feeds, sleep, and what’s next — your baby day at a glance."
          linkLabel="See baby tracker →"
          onLink={() => nav('/demo/motherhood/baby')}
          rows={[
            { label: "Baby's age", value: '6 wks · Born Feb 9' },
            { label: 'Feeds today', value: '7 feeds' },
            { label: 'Sleep last night', value: '4.5 hrs (2 stretches)' },
            { label: 'Next appt', value: '6-week check-up' },
          ]}
        />
        <ProgressWidget
          theme={t}
          title="Postpartum recovery · week 6"
          descriptor="6-week check-up · physical + mental screening"
          steps={[
            { number: 1, label: 'Wk 1–2', done: true },
            { number: 2, label: 'Wk 3–4', done: true },
            { number: 3, label: 'Wk 5', done: true },
            { number: 4, label: '6-wk appt', active: true },
            { number: 5, label: 'Activity' },
            { number: 6, label: 'New normal' },
          ]}
          linkLabel="My journey →"
          onLink={() => nav('/demo/motherhood/myjourney')}
        />
      </div>
      <div style={{ ...gridStyle(m), marginTop: 16 }}>
        <WomenLikeYou
          theme={t}
          descriptor="Moms at a similar postpartum stage — matched by Nova, not random."
          users={[
            { name: 'Jasmine T.', description: '6 weeks postpartum · Columbus OH', initial: 'J' },
            { name: 'Priya L.', description: '8 weeks · breastfeeding · Denver', initial: 'P' },
          ]}
          onSeeAll={() => nav('/demo/motherhood/connect')}
        />
        <LiveRooms
          theme={t}
          descriptor="Live support rooms — fourth trimester, feeding, sleep, and more."
          rooms={[
            { name: 'Fourth trimester support', count: 22 },
            { name: 'Breastfeeding questions', count: 11 },
          ]}
          onSeeAll={() => nav('/demo/motherhood/community')}
        />
      </div>
      <CommunityPreview
        theme={t}
        descriptor="Honest posts from your motherhood chapter — you’re not the only one."
        posts={[
          { authorName: 'Sofia M.', authorInitial: 'S', timeAgo: '3h ago', text: "Week 6 and I finally cried at my OB when she asked how I was really doing — not how the baby was. Please ask. 💜", likes: 412, sameBoat: 287, replies: 56 },
          { authorName: 'Maya', authorInitial: 'M', timeAgo: '5h ago', text: "Sofia had her first real smile today. I was so exhausted I almost missed it. These tiny moments are the whole thing. 🤱", likes: 567, sameBoat: 143, replies: 34 },
        ]}
        onSeeAll={() => nav('/demo/motherhood/community')}
      />
    </>
  );
}

export function MenopauseHomePage({ navigate, m, hideTopHeader }) {
  const t = CHAPTER_THEMES.menopause;
  const nav = navigate || (() => {});
  return (
    <>
      {!hideTopHeader && (
        <>
          <HomePageHeader pageTitle="Your Menopause Hub" theme={t} statusPillText="Tracking symptoms · 14 days logged" onSignUp={nav} />
          <HeroStatusCard
            theme={t}
            eyebrow="PERIMENOPAUSE · MONTH 3 OF TRACKING"
            headline="Nova has been watching your patterns, Riley."
            subline="14 days logged · 3 symptoms tracked · Next OB appointment Apr 8"
            pills={[
              { label: 'Perimenopause', dot: true, dotColor: 'bg-pink-400' },
              { label: '14 days tracked' },
              { label: 'Appointment Apr 8' },
            ]}
            linkLabel="My journey →"
            onLink={() => nav('/demo/menopause/myjourney')}
          />
          <NovaCheckIn
            theme={t}
            context="pattern update"
            message="Based on your last 14 days, your hot flashes peak between 6–8pm. I've prepared a symptom summary for Dr. Chang on Apr 8."
            onOpenNova={() => nav('/nova')}
          />
        </>
      )}
      <div style={{ ...gridStyle(m), marginTop: 16 }}>
        <PrimaryWidget
          theme={t}
          title="Today’s symptoms"
          descriptor="What you’ve logged lately — Nova uses this for patterns and doctor prep."
          linkLabel="Log symptoms →"
          onLink={() => nav('/demo/menopause/myjourney')}
          rows={[
            { label: 'Hot flashes', value: '3 today' },
            { label: 'Sleep quality', value: 'Not logged yet' },
            { label: 'Mood', value: 'Not logged yet' },
            { label: 'HRT', value: 'Not started' },
          ]}
        />
        <ProgressWidget
          theme={t}
          title="Tracking · month 3"
          descriptor="14 days logged · appointment prep ready"
          steps={[
            { number: 1, label: 'Start', done: true },
            { number: 2, label: 'Wk 1–2', done: true },
            { number: 3, label: 'Patterns', active: true },
            { number: 4, label: 'Dr. appt' },
            { number: 5, label: 'HRT eval' },
            { number: 6, label: 'Adjust' },
          ]}
          linkLabel="My journey →"
          onLink={() => nav('/demo/menopause/myjourney')}
        />
      </div>
      <div style={{ ...gridStyle(m), marginTop: 16 }}>
        <WomenLikeYou
          theme={t}
          descriptor="Women on a similar path — symptoms, HRT, and honest check-ins."
          users={[
            { name: 'Linda K.', description: 'Perimenopause month 5 · Denver', initial: 'L' },
            { name: 'Diane R.', description: 'HRT month 2 · Chicago · sleep improving', initial: 'D' },
          ]}
          onSeeAll={() => nav('/demo/menopause/connect')}
        />
        <LiveRooms
          theme={t}
          descriptor="Live rooms for symptoms, sleep, and treatment questions."
          rooms={[
            { name: 'Hot flash management', count: 16 },
            { name: 'HRT questions & support', count: 11 },
          ]}
          onSeeAll={() => nav('/demo/menopause/community')}
        />
      </div>
      <CommunityPreview
        theme={t}
        descriptor="Stories and wins from your menopause chapter."
        posts={[
          { authorName: 'Linda K.', authorInitial: 'L', timeAgo: '1h ago', text: 'Brought a symptom log to my appointment and my doctor finally took me seriously. Nova helped me organize it. Game changer. 🍂', likes: 89, sameBoat: 67, replies: 23 },
          { authorName: 'Diane R.', authorInitial: 'D', timeAgo: '6h ago', text: 'Month 2 on HRT and sleeping through the night for the first time in 8 months. Ask your doctor.', likes: 203, sameBoat: 115, replies: 41 },
        ]}
        onSeeAll={() => nav('/demo/menopause/community')}
      />
    </>
  );
}

export function FirstChapterHomePage({ navigate, m, hideTopHeader }) {
  const t = CHAPTER_THEMES['first-chapter'];
  const nav = navigate || (() => {});
  return (
    <>
      {!hideTopHeader && (
        <>
          <HomePageHeader pageTitle="Your First Chapter Hub" theme={t} statusPillText="Cycle day 14 · Learning about your body" onSignUp={nav} />
          <HeroStatusCard
            theme={t}
            eyebrow="FIRST CHAPTER · CYCLE DAY 14"
            headline="Day 14 of your cycle, Maya."
            subline="Middle of your cycle · Energy tends to peak now · Log how you feel today"
            pills={[
              { label: 'Cycle day 14', dot: true, dotColor: 'bg-rose-300' },
              { label: '3-day mood streak' },
              { label: 'Regular cycle' },
            ]}
            linkLabel="My cycle →"
            onLink={() => nav('/demo/first-chapter/myjourney')}
          />
          <NovaCheckIn
            theme={t}
            chapter="ttc"
            context="hey 👋"
            message="You're on day 14 — roughly the middle of your cycle. Energy is usually highest now. How are you feeling today?"
            onOpenNova={() => nav('/nova')}
          />
        </>
      )}
      <div style={{ ...gridStyle(m), marginTop: 16 }}>
        <PrimaryWidget
          theme={t}
          title="Your cycle"
          descriptor="Length, timing, and how you’ve been feeling — your body, in plain numbers."
          linkLabel="See period tracker →"
          onLink={() => nav('/demo/first-chapter/myjourney')}
          rows={[
            { label: 'Days since last period', value: '14' },
            { label: 'Cycle length', value: '28 days' },
            { label: 'Next period est.', value: '~Apr 6' },
            { label: 'Mood streak', value: '3 days 🌙' },
          ]}
        />
        <ProgressWidget
          theme={t}
          title="Cycle phase · ovulation"
          descriptor="Day 14 · follicular phase ending"
          steps={[
            { number: 1, label: 'Period', done: true },
            { number: 2, label: 'Follicular', done: true },
            { number: 3, label: 'Ovulation', active: true },
            { number: 4, label: 'Luteal' },
            { number: 5, label: 'Next period' },
          ]}
          linkLabel="Learn more →"
          onLink={() => nav('/demo/first-chapter/learn')}
        />
      </div>
      <div style={{ ...gridStyle(m), marginTop: 16 }}>
        <WomenLikeYou
          theme={t}
          descriptor="People your age learning their cycles — Nova matched you on chapter and goals."
          users={[
            { name: 'Emma T.', description: 'Cycle day 14 · learning her cycle · Portland', initial: 'E' },
            { name: 'Lia K.', description: '28-day cycle · mood tracking · Austin', initial: 'L' },
          ]}
          onSeeAll={() => nav('/demo/first-chapter/connect')}
        />
        <LiveRooms
          theme={t}
          descriptor="Safe live rooms for questions you might not want to Google."
          rooms={[
            { name: 'Cycle tracking beginners', count: 9 },
            { name: 'Talk to my mom about periods', count: 6 },
          ]}
          onSeeAll={() => nav('/demo/first-chapter/community')}
        />
      </div>
      <CommunityPreview
        theme={t}
        descriptor="Posts from your First Chapter — normal questions, real answers."
        posts={[
          { authorName: 'Emma T.', authorInitial: 'E', timeAgo: '2h ago', text: 'Finally understanding what ovulation feels like after 3 months of tracking. The Nova explanations are so much clearer than school 🌙', likes: 24, sameBoat: 18, replies: 7 },
          { authorName: 'Lia K.', authorInitial: 'L', timeAgo: '5h ago', text: 'My cramps were SO bad this cycle. Nova suggested I track the severity and bring it to my doctor. Anyone else done this?', likes: 31, sameBoat: 29, replies: 12 },
        ]}
        onSeeAll={() => nav('/demo/first-chapter/community')}
      />
    </>
  );
}
